import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { LoaderService } from "./loader/loader.service";
import { MainState } from '../../store/main/main.reducer';
import { BehaviorSubject, EMPTY, merge, Observable, of, Subject, timer } from "rxjs";
import { UVNotification, UVNotificationCreateRequest, UVNotificationDetail } from "../models/notification";
import { catchError, distinctUntilChanged, filter, map, share, switchMap, take, takeUntil, tap } from "rxjs/operators";
import * as MainActions from '../../store/main/main.actions';
import { PassDataService } from "./pass-data/pass-data.service";
import { AuthState } from '../../store/auth/auth.reducer';
import * as AuthSelectors from '../../store/auth/auth.selectors';

/** Max popups per refresh cycle; extras stay in the bell. Prevents stacking
 *  after a reconnect or bulk-create burst. */
const MAX_LIVE_TOASTS_PER_CYCLE = 1;

@Injectable({
  providedIn: 'root'
})
export class NotificationApiService implements OnDestroy {

  private notificationsList$: Observable<{ Notifications: UVNotification[] }>;
  private stopPolling: Subject<void> = new Subject<void>();
  private readonly refreshTrigger: Subject<void> = new Subject<void>();
  public pollingSeconds: number;

  /** `null` = baseline not yet captured. Reset on user or domain change so
   *  backlog unreads never toast after re-login or in the new domain. */
  private seenIds: Set<string> | null = null;
  private readonly liveArrivalsSubject = new Subject<UVNotification[]>();
  /** Emits only notifications new since the previous fetch (for toast/popup). */
  public readonly liveArrivals$: Observable<UVNotification[]> = this.liveArrivalsSubject.asObservable();

  /** Held until the bell takes it: a push tapped at cold start asks before the header exists. */
  private readonly bellOpenRequested = new BehaviorSubject<boolean>(false);
  public readonly bellOpenRequests$: Observable<boolean> = this.bellOpenRequested.pipe(filter(Boolean));

  constructor(
    private http: HttpClient,
    private readonly loaderService: LoaderService,
    private store: Store<MainState>,
    private passDataService: PassDataService,
    private authStore: Store<AuthState>
  ) {

    // Reset baseline on user or domain change → the next fetch sets a fresh
    // baseline → no toasts for notifications created while logged out or
    // already seen from the previous domain.
    merge(
      this.authStore.select(AuthSelectors.getUser).pipe(map(u => u?.Id ?? null), distinctUntilChanged()),
      this.authStore.select(AuthSelectors.getDomain).pipe(map(d => d?.Id ?? null), distinctUntilChanged())
    ).pipe(
      takeUntil(this.stopPolling)
    ).subscribe(() => {
      this.seenIds = null;
    });

    // Polling fallback (if a SignalR ping is missed) + immediate re-fetch on triggerRefresh().
    this.notificationsList$ = this.passDataService.appConfiguration$.pipe(
      filter(config => !!config),
      take(1),
      map(config => {
        const seconds = +config?.Settings?.notification_polling_seconds;
        return seconds > 0 ? seconds : 60;
      }),
      switchMap(seconds => {
        this.pollingSeconds = seconds;
        return merge(timer(1, seconds * 1000), this.refreshTrigger);
      }),
      // A failed poll (offline) skips a tick instead of ending the stream.
      switchMap(() => this.http.get<any>(`uv.notification.list.api`).pipe(catchError(() => EMPTY))),
      tap((res: { Notifications: UVNotification[] }) =>
        this.detectLiveArrivals(res?.Notifications ?? [])
      ),
      share(),
      takeUntil(this.stopPolling)
    );
  }

  /** Diff fetched IDs vs previous: first call = baseline (no emit),
   *  subsequent calls emit additions only. */
  private detectLiveArrivals(notifications: UVNotification[]): void {
    const currentIds = new Set(notifications.map(n => n.Id));

    if (this.seenIds === null) {
      this.seenIds = currentIds;
      return;
    }

    const newArrivals = notifications.filter(n => !this.seenIds!.has(n.Id));
    this.seenIds = currentIds;

    if (newArrivals.length === 0) return;

    const toast = newArrivals
      .slice()
      .sort((a, b) => (b.Created ?? 0) - (a.Created ?? 0))
      .slice(0, MAX_LIVE_TOASTS_PER_CYCLE);

    this.liveArrivalsSubject.next(toast);
  }

  public getNotificationsList(): Observable<{ Notifications: UVNotification[] }> {
    return this.notificationsList$;
  }

  /** Force an immediate re-fetch on the shared stream (used by SignalR ping). */
  public triggerRefresh(): void {
    this.refreshTrigger.next();
  }

  public requestBellOpen(): void {
    this.bellOpenRequested.next(true);
  }

  public bellOpenHandled(): void {
    this.bellOpenRequested.next(false);
  }

  /** The next fetch becomes the baseline: an alert just opened from a push does not pop up again. */
  public skipLiveArrivals(): void {
    this.seenIds = null;
  }

  /** Outside the polling stream, so it never counts as a live arrival. */
  public getUnreadNotifications(): Observable<UVNotification[]> {
    return this.http.get<{ Notifications: UVNotification[] }>(`uv.notification.list.api`).pipe(
      map(res => res?.Notifications ?? []),
      catchError(() => of([] as UVNotification[]))
    );
  }

  public readNotification(notificationId: string): Observable<any> {
    return this.http.post<any>(
      `uv.notification.read.api`,
      {
        id: notificationId
      }
    ).pipe(
      catchError(err => {
        this.loaderService.hide()
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    )
  }

  public getNotificationsHistory(): Observable<{ Notifications: UVNotification[] }> {
    return this.http.get<any>(`uv.notification.list.api?includeRead=true`).pipe(
      catchError(err => {
        this.loaderService.hide();
        this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' }));
        return of({ Notifications: [] as UVNotification[] });
      })
    );
  }

  public markAllAsRead(): Observable<any> {
    return this.http.post<any>(`uv.notification.readall.api`, {}).pipe(
      catchError(err => {
        this.loaderService.hide();
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    );
  }

  public getAllNotificationsList(): Observable<UVNotification[]> {
    return this.http.get<any>(
      `uv.notification.all.api`
    ).pipe(
      map((res: any) => res.Notifications),
      catchError(err => {
        this.loaderService.hide()
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    )
  }

  public notificationDetails(notificationId: string): Observable<UVNotificationDetail[]> {
    return this.http.get<any>(
      `uv.notification.detail.api?id=${notificationId}`
    ).pipe(
      map((res: any) => res.NotificationDetails),
      catchError(err => {
        this.loaderService.hide()
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    )
  }

  public expireNotification(notificationId: string): Observable<any> {
    return this.http.post<any>(
      `uv.notification.expire.api`,
      {
        id: notificationId
      }
    ).pipe(
      catchError(err => {
        this.loaderService.hide()
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    )
  }

  public createNotification(request: UVNotificationCreateRequest): Observable<any> {
    return this.http.post<any>(
      `uv.notification.create.api`,
      request
    ).pipe(
      catchError(err => {
        this.loaderService.hide()
        return of(this.store.dispatch(MainActions.setError({ error: err?.error?.Message ? err.error.Message : 'customError' })));
      })
    )
  }

  ngOnDestroy(): void {
    this.stopPolling.next();
    this.stopPolling.complete();
  }

}
